<script setup>

    import { useRouter } from 'vue-router'

    import { computed } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useEngagementStore, useAuthStore } from '@/Stores'

    import Tag from '@/Components/Tag.vue'
    import { PencilSquareIcon } from '@heroicons/vue/20/solid'
    import { fullName } from '@/Helpers'

    const engagementStore = useEngagementStore()
    const { scheduledEngagementData, showAllScheduledRequests, loading } = storeToRefs(engagementStore)
    const authStore = useAuthStore()
    const { user } = storeToRefs(authStore)

    const props = defineProps({
        scope: {
            type: Array,
            default: () => [ 'all' ] // Possible values: 'leads', 'prospects', 'clients', 'all'
        }
    })

    const router = useRouter()

    function onRowSelect(event) {
        engagementStore.selectRequest(event.data.request_id)
    }

    const transformScheduledEngagementData = computed(() => {
        return scheduledEngagementData.value.map(request => {

            // Build the location string from address
            let location = '';
            if (request.address) {
                const parts = [ request.address.city, request.address.state ].filter(Boolean);
                location = parts.join(', ');
            }

            // Transform the data to match the expected format
            const transformedRecipient = {
                request_id: request.id,
                idNumber: request.recipient.id,
                first_name: request.recipient.first_name,
                last_name: request.recipient.last_name,
                type: request.recipient.type,
                name: `${request.recipient.first_name} ${request.recipient.last_name}`.trim(),
                location: location,
                address: {
                    address: request.address || '',
                    address_2: request.address_2 || '',
                    city: request.city || '',
                    state: request.state || '',
                    zip: request.zip || ''
                },
                latest_rating_per_job: request.recipient.latest_rating_per_job || [],
                senders: request.from || []
            }

            return transformedRecipient;
        })
    })

    const filteredScheduledEngagementData = computed(() => {
        return transformScheduledEngagementData.value.filter(engagement => {
            if (showAllScheduledRequests.value === false) {
                return engagement.senders.some(sender => sender.id === user.value.user_id);
            } else {
                return true;
            }
        })
    })


</script>

<template>

    <DataTable
        :value="filteredScheduledEngagementData"
        :pt="{
            root: 'text-xs',
            tablecontainer: '!overflow-visible',
            thead: 'sticky top-28 z-10 bg-white/75 backdrop-blur backdrop-filter',
        }"
        :loading="loading"
        selectionMode="single"
        @rowSelect="onRowSelect"
        stripedRows
    >

        <template #empty> No Requests Found </template>

        <Column
            header="id"
            field="id"
            class="min-w-5"
        >
            <template #body="{ data }">
                <span class="font-mono text-sm">{{ data.request_id }}</span>
            </template>
        </Column>

        <Column
            v-if="props.scope === 'all'"
            header="Type"
            field="type"
            class="w-[8%]"
      >
            <template #body="{ data }">
                <span class="capitalize">{{ data.type }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder="Search by type" />
            </template>
        </Column>

        <Column
            header="RECIPIENT"
            field="name"
            class="w-[37%]"
        >
            <template #body="{ data }">
                <div class="flex gap-2">
                    <span class="text-base font-semibold text-lifeworx-blue-900 text-nowrap">
                        {{ fullName(data.first_name, data.last_name) }}
                    </span>

                    <Tag
                        v-if="data.type == 'client'"
                        class="text-xs"
                        color="green"
                    >
                        {{ data.type }}
                    </Tag>
                    <Tag
                        v-if="data.type === 'caregiver'"
                        class="text-xs"
                        color="blue"
                    >
                        {{ data.type }}
                    </Tag>
                    <Tag
                        v-if="data.type == 'contact'"
                        class="text-xs"
                        color="yellow"
                    >
                        {{ data.type }}
                    </Tag>
                    <Tag
                        v-if="data.type === 'coi'"
                        class="text-xs"
                        color="purple"
                    >
                        COI
                    </Tag>
                </div>
            </template>
        </Column>

        <Column
            header="LOCATION"
            field="address"
            class="w-[35%]"
        >
            <template #body="{ data }">
                <span class="text-sm font-semibold text-stone-400">{{ data.location }}</span>
            </template>
        </Column>

        <Column
            header="SENDERS"
            field="senders"
            class="w-[35%]"
        >
            <template #body="{ data }">
                <span class="text-sm font-semibold text-lifeworx-blue-800 flex gap-2">
                    <template v-for="(sender, index) in data.senders" :key="index">
                        <!-- <LatteStaffLabel
                            :actionable="false"
                            :staff="sender"
                            size="sm"
                        /> -->
                        {{ sender.first_name }}<template v-if="index < data.senders.length - 1">, </template>
                    </template>
                </span>
            </template>
        </Column>

        <Column
            header="ACTION"
            class="hidden md:table-cell w-20 lg:w-60"
        >
            <template #body="{ data }">
                <span class="flex items-center text-sm text-lifeworx-blue-800 font-semibold hover:text-lifeworx-blue-600 gap-2">
                    <PencilSquareIcon class="inline-block w-6 h-6" /> Edit
                </span>
            </template>
        </Column>

    </DataTable>

</template>
